import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView, useReducedMotion } from 'motion/react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollReveal, TextReveal } from '@/components/motion'
import { agents } from '@/components/landing/AITeamOrbit'
import { industriesData } from '@/data/industries'

const byName = Object.fromEntries(agents.map((a) => [a.name, a]))

type Line = { from: 'You' | (typeof agents)[number]['name']; text: string }

/**
 * One everyday document task per industry, worked by the six core agents.
 * Sample conversations: they show how the agents divide the work, not
 * customer results.
 */
const SCENARIOS: Array<{ slug: string; task: string; lines: Line[] }> = [
  {
    slug: 'legal',
    task: 'Engagement letter',
    lines: [
      { from: 'You', text: 'Draft an engagement letter for a new commercial client.' },
      { from: 'Dimi', text: 'Draft ready from your engagement-letter template, client details filled in.' },
      { from: 'Alex', text: 'Added your standard limitation-of-liability clause from the clause library.' },
      { from: 'Scarlet', text: "Checked against your firm's approved wording and style. Ready for partner review." },
    ],
  },
  {
    slug: 'finance',
    task: 'Quarterly board report',
    lines: [
      { from: 'You', text: 'Prepare the Q3 board report.' },
      { from: 'Andrew', text: 'Pulled the Q3 figures from the finance workbook and built the variance tables.' },
      { from: 'Dimi', text: 'Narrative sections drafted around the biggest variances.' },
      { from: 'Scarlet', text: 'Fonts, number formats and disclaimers match your reporting template.' },
    ],
  },
  {
    slug: 'healthcare',
    task: 'Discharge letter',
    lines: [
      { from: 'You', text: 'Write the discharge letter for the GP, with a patient copy.' },
      { from: 'Dimi', text: 'Letter drafted from your discharge template.' },
      { from: 'Sia', text: 'Patient copy rewritten in plain language.' },
      { from: 'Scarlet', text: 'All required sections are present in both versions.' },
    ],
  },
  {
    slug: 'insurance',
    task: 'Policy renewal',
    lines: [
      { from: 'You', text: 'Renewal letter for a commercial property policy.' },
      { from: 'Dimi', text: 'Letter built from the renewal template, policy schedule attached.' },
      { from: 'Olga', text: 'Similar renewals included a risk-improvement summary. Add it?' },
      { from: 'Scarlet', text: 'Wording matches your approved product language.' },
    ],
  },
  {
    slug: 'engineering',
    task: 'RFI response',
    lines: [
      { from: 'You', text: 'Respond to RFI 042 on the steel connection details.' },
      { from: 'Alex', text: 'Found the relevant spec sections and two earlier RFIs on the same detail.' },
      { from: 'Dimi', text: 'Response drafted on your RFI template.' },
      { from: 'Scarlet', text: 'Drawing references and document numbering checked.' },
    ],
  },
  {
    slug: 'consulting',
    task: 'Client proposal',
    lines: [
      { from: 'You', text: 'Proposal for a retail client: supply-chain review.' },
      { from: 'Dimi', text: 'Proposal drafted from your strongest past proposals.' },
      { from: 'Olga', text: 'Your best proposals open with the client’s problem. I moved it up.' },
      { from: 'Sia', text: 'Executive summary tightened to one page.' },
    ],
  },
]

/** The conversation for one scenario, lines arriving one after another. */
function Conversation({ lines, play }: { lines: Line[]; play: boolean }) {
  const reduce = useReducedMotion()
  return (
    <ol className="flex flex-col gap-3">
      {lines.map((line, i) => {
        const you = line.from === 'You'
        const agent = you ? undefined : byName[line.from]
        return (
          <motion.li
            key={i}
            className={cn('flex items-start gap-3', you && 'flex-row-reverse')}
            initial={false}
            animate={reduce || play ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1], delay: reduce ? 0 : 0.15 + i * 0.45 }}
          >
            {agent ? (
              <span className="h-10 w-10 shrink-0 overflow-hidden" style={{ backgroundColor: agent.color }}>
                <img src={agent.avatar} alt="" className="h-full w-full translate-y-[6%] scale-110 object-cover" />
              </span>
            ) : (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-[#0a0a0a] text-xs font-bold uppercase text-white dark:bg-white dark:text-[#0a0a0a]">
                You
              </span>
            )}
            <p
              className={cn(
                'min-w-0 max-w-[85%] px-4 py-3 text-[15px] leading-snug',
                you
                  ? 'bg-[var(--color-landing-accent)] text-white'
                  : 'bg-white text-[var(--color-landing-gray-900)] dark:bg-[#1c1c26] dark:text-white'
              )}
            >
              {agent && (
                <span className="mb-1 block text-xs font-bold" style={{ color: agent.color }}>
                  {agent.name} · {agent.role}
                </span>
              )}
              {line.text}
            </p>
          </motion.li>
        )
      })}
    </ol>
  )
}

/**
 * Real work, by industry: tabs for the six industries; each plays one
 * everyday document task through the agents and links to its industry page.
 * Replaces the scenario strip and the industry grid.
 */
export function IndustryScenarios() {
  const [active, setActive] = useState(0)
  const [run, setRun] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.3 })
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  // Replay the conversation each time a tab is chosen.
  useEffect(() => setRun((r) => r + 1), [active])

  const scenario = SCENARIOS[active]!
  const industry = industriesData[scenario.slug]!

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const next = (active + step + SCENARIOS.length) % SCENARIOS.length
    setActive(next)
    tabRefs.current[next]?.focus()
  }

  return (
    <section aria-labelledby="scenarios-title" className="landing-section bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
      <div className="landing-container">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-end lg:gap-20">
          <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="scenarios-title">
              Real work, <span className="landing-highlight">by industry.</span>
            </span>
          </TextReveal>
          <ScrollReveal direction="none" delay={0.15}>
            <p className="landing-text-body-lg max-w-[48ch] border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
              Pick your industry and watch the agents work through an everyday document task.
            </p>
          </ScrollReveal>
        </div>

        <div role="tablist" aria-label="Industries" onKeyDown={onKeyDown} className="mt-12 flex flex-wrap gap-2 md:mt-16">
          {SCENARIOS.map((s, i) => {
            const selected = i === active
            return (
              <button
                key={s.slug}
                ref={(el) => {
                  tabRefs.current[i] = el
                }}
                role="tab"
                id={`scenario-tab-${s.slug}`}
                aria-selected={selected}
                aria-controls="scenario-panel"
                tabIndex={selected ? 0 : -1}
                onClick={() => setActive(i)}
                className={cn(
                  'h-11 px-5 font-[family-name:var(--font-landing-subdisplay)] text-sm font-bold uppercase transition-colors duration-200',
                  selected
                    ? 'bg-[var(--color-landing-accent)] text-white'
                    : 'bg-[#0a0a0a]/5 text-[var(--color-landing-gray-700)] hover:bg-[#0a0a0a]/10 dark:bg-white/10 dark:text-[var(--color-landing-gray-300)] dark:hover:bg-white/15'
                )}
              >
                {industriesData[s.slug]!.shortName}
              </button>
            )
          })}
        </div>

        <div
          ref={ref}
          id="scenario-panel"
          role="tabpanel"
          aria-labelledby={`scenario-tab-${scenario.slug}`}
          className="mt-6 grid grid-cols-1 gap-10 bg-[#0a0a0a]/[0.04] p-6 dark:bg-white/[0.04] md:p-10 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:gap-16"
        >
          <div key={run}>
            <Conversation lines={scenario.lines} play={inView} />
          </div>
          <div className="flex flex-col justify-between gap-8">
            <div>
              <p className="font-[family-name:var(--font-landing-display)] text-[clamp(1.75rem,3vw,2.75rem)] uppercase leading-[1.3] text-[var(--color-landing-gray-900)] dark:text-white">
                {scenario.task}
              </p>
              <p className="landing-text-body mt-4 max-w-[44ch] text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                {industry.subheadline}
              </p>
            </div>
            <Link
              to={`/industries/${scenario.slug}`}
              className="landing-cta landing-cta--solid landing-cta--md landing-btn-sweep self-start"
            >
              Explore {industry.shortName}
              <ArrowRight aria-hidden className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

export default IndustryScenarios
